import { apiFetch } from '../api';

/** A module's declared levels and off-ladder permissions, from its spec. */
export interface AdminModule {
  id: string;
  name: string;
  icon: string;
  levels: { key: string; label: string }[];
  /** Permissions belonging to no level — rendered as checkboxes. */
  offLadder: { permission: string; label: string; description: string | null }[];
}

/** What a principal may do in one module. */
export interface ModuleAccess {
  moduleId: string;
  moduleName: string;
  /** Matching level key, or null when only extras (or nothing) are held. */
  level: string | null;
  extras: string[];
  /** The held permissions match no level exactly — shown as "Custom". */
  custom: boolean;
}

export interface AdminGroupSummary {
  id: string;
  name: string;
  description: string | null;
  source: 'local' | 'workspace';
  /** Built-in and undeletable (All Users, Administrators). */
  system: boolean;
  memberCount: number;
  access: ModuleAccess[];
}

export interface AdminGroupDetail extends Omit<AdminGroupSummary, 'memberCount'> {
  /** False for All Users (membership is automatic) and mirrored groups. */
  membershipEditable: boolean;
  members: { id: string; name: string; email: string }[];
}

export interface AdminUser {
  id: string;
  name: string;
  email: string;
  groups: { id: string; name: string }[];
}

export interface AdminUserAccess extends AdminUser {
  permissions: string[];
  access: (ModuleAccess & { viaGroups: { id: string; name: string }[] })[];
}

export interface AdminPublicRoute {
  id: string;
  method: string;
  path: string;
  description: string | null;
}

/** One module's desired access, as the editor submits it. */
export interface AccessInput {
  moduleId: string;
  level: string | null;
  extras: string[];
}

export const adminApi = {
  modules: () => apiFetch<AdminModule[]>('/api/admin/modules'),
  publicRoutes: () => apiFetch<AdminPublicRoute[]>('/api/admin/public-routes'),

  groups: () => apiFetch<AdminGroupSummary[]>('/api/admin/groups'),
  group: (id: string) => apiFetch<AdminGroupDetail>(`/api/admin/groups/${encodeURIComponent(id)}`),
  createGroup: (body: { name: string; description?: string }) =>
    apiFetch<{ id: string }>('/api/admin/groups', { method: 'POST', body: JSON.stringify(body) }),
  updateGroup: (id: string, body: { name?: string; description?: string }) =>
    apiFetch<void>(`/api/admin/groups/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),
  deleteGroup: (id: string) =>
    apiFetch<void>(`/api/admin/groups/${encodeURIComponent(id)}`, { method: 'DELETE' }),

  addMember: (groupId: string, userId: string) =>
    apiFetch<void>(`/api/admin/groups/${encodeURIComponent(groupId)}/members`, {
      method: 'POST',
      body: JSON.stringify({ userId }),
    }),
  removeMember: (groupId: string, userId: string) =>
    apiFetch<void>(
      `/api/admin/groups/${encodeURIComponent(groupId)}/members/${encodeURIComponent(userId)}`,
      { method: 'DELETE' },
    ),

  setAccess: (groupId: string, access: AccessInput[]) =>
    apiFetch<void>(`/api/admin/groups/${encodeURIComponent(groupId)}/access`, {
      method: 'PUT',
      body: JSON.stringify({ access }),
    }),

  users: () => apiFetch<AdminUser[]>('/api/admin/users'),
  createUser: (body: { email: string; firstName: string; lastName: string }) =>
    apiFetch<AdminUser>('/api/admin/users', { method: 'POST', body: JSON.stringify(body) }),
  userAccess: (id: string) =>
    apiFetch<AdminUserAccess>(`/api/admin/users/${encodeURIComponent(id)}/access`),

};
